import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfileStore } from '@/store'
import type {
	VereAgirAllListOutput,
	VereAgirAllListParams,
	VereAgirListOutput,
	VereAgirListParams,
	VereAgirMeListOutput,
	VereAgirMeListParams,
	VereAgirOutput,
	VereAgirUpdateInput,
} from '@/types'
import {
	createVereAgir,
	deleteVereAgir,
	getVereAgirAllList,
	getVereAgirById,
	getVereAgirList,
	getVereAgirMeList,
	updateVereAgir,
} from '../services/ver-e-agir'

const QUERY_KEY = ['vere-agir']
const ME_QUERY_KEY = ['vere-agir', 'me', 'list']
const PENDENCIES_QUERY_KEY = ['pendencies', 'me']

export function useCreateVereAgir() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: createVereAgir,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: PENDENCIES_QUERY_KEY })
		},
	})
}

export function useUpdateVereAgir() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: VereAgirUpdateInput }) =>
			updateVereAgir(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: PENDENCIES_QUERY_KEY })
		},
	})
}

export function useDeleteVereAgir() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: deleteVereAgir,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: PENDENCIES_QUERY_KEY })
		},
	})
}

export function useVereAgirList(params?: VereAgirListParams) {
	return useQuery<VereAgirListOutput, Error>({
		queryKey: [...QUERY_KEY, 'list', params],
		queryFn: () => getVereAgirList(params),
		staleTime: 10 * 60 * 1000, // 10 minutos
	})
}

export function useVereAgirMeList(params?: VereAgirMeListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: VereAgirMeListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<VereAgirMeListOutput, Error>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getVereAgirMeList(scopedParams),
		staleTime: 10 * 60 * 1000,
	})
}

export function useVereAgirAllList(params?: VereAgirAllListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: VereAgirAllListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<VereAgirAllListOutput, Error>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getVereAgirAllList(scopedParams),
		staleTime: 10 * 60 * 1000,
	})
}

export function useVereAgirById(id?: string) {
	return useQuery<VereAgirOutput, Error>({
		queryKey: [...QUERY_KEY, id],
		queryFn: () => {
			if (!id) {
				throw new Error('VereAgir ID is required')
			}
			return getVereAgirById(id).then((data) => ({
				...data,
				imagePath: data.imagePath ?? null,
			}))
		},
		enabled: Boolean(id),
		staleTime: 10 * 60 * 1000,
	})
}
